import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import { API_ENDPOINTS } from '../../utils/config';
import {
  Users,
  UserPlus,
  Search,
  Edit,
  Trash2,
  Eye,
  Filter,
  X,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw
} from 'lucide-react';

const CoordinatorClientAssignment = () => {
  // State management
  const [assignments, setAssignments] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [clients, setClients] = useState([]);
  const [unassignedClients, setUnassignedClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCoordinators, setIsLoadingCoordinators] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedCoordinator, setSelectedCoordinator] = useState(null);

  // Form states
  const [createForm, setCreateForm] = useState({
    coordinator_id: '',
    client_ids: [],
    notes: ''
  });
  const [editForm, setEditForm] = useState({
    coordinator_id: '',
    client_ids: [],
    notes: '',
    is_active: true
  });

  // Filter states
  const [filters, setFilters] = useState({
    coordinator_id: '',
    client_id: '',
    is_active: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState(null);

  // Get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  // Silent axios get for optional endpoints (suppresses console errors)
  const silentAxiosGet = async (url, config = {}) => {
    // Store original console methods
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;
    const originalConsoleLog = console.log;
    
    // Suppress all console output temporarily for this request
    const noop = () => {};
    console.error = noop;
    console.warn = noop;
    console.log = noop;
    
    try {
      const response = await axios.get(url, {
        ...config,
        validateStatus: (status) => {
          // Accept all status codes < 500 to prevent axios from throwing
          // This prevents axios from logging errors for 4xx responses
          return status < 500;
        }
      });
      
      // Restore console methods
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
      console.log = originalConsoleLog;
      
      return response;
    } catch (err) {
      // Restore console methods
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
      console.log = originalConsoleLog;
      
      // For 4xx errors, return a mock response instead of throwing
      if (err?.response?.status >= 400 && err?.response?.status < 500) {
        return { 
          status: err.response.status, 
          data: null,
          headers: err.response?.headers || {}
        };
      }
      // Re-throw only for 5xx errors or network errors
      throw err;
    }
  };

  // Get error message helper
  const getErrorMessage = (err, fallback) => {
    const respData = err?.response?.data;
    return (
      respData?.message ||
      respData?.detail ||
      respData?.error ||
      (typeof respData === 'string' ? respData : null) ||
      err?.message ||
      fallback
    );
  };

  // Fetch coordinators (users with coordinator role)
  const fetchCoordinators = async () => {
    setIsLoadingCoordinators(true);
    try {
      const headers = getAuthHeaders();
      // Fetch user roles and filter for coordinator role
      const userRolesResponse = await axios.get(API_ENDPOINTS.USER_ROLES_LIST, { headers });
      const userRolesData = Array.isArray(userRolesResponse.data)
        ? userRolesResponse.data
        : (userRolesResponse.data?.results || userRolesResponse.data?.data || userRolesResponse.data?.user_roles || []);

      // Filter users with coordinator role (case-insensitive)
      const coordinatorUsers = userRolesData
        .filter(ur => {
          const roleName = ur?.role_detail?.name || ur?.role?.name || ur?.role || '';
          return roleName.toLowerCase().includes('coordinator');
        })
        .map(ur => ({
          id: ur?.user_detail?.id || ur?.user?.id,
          username: ur?.user_detail?.username || ur?.user?.username,
          email: ur?.user_detail?.email || ur?.user?.email,
          is_active: ur?.user_detail?.is_active !== false
        }))
        .filter(c => c.id && c.is_active);

      setCoordinators(coordinatorUsers);
    } catch (err) {
      console.error('Error fetching coordinators:', err);
      setCoordinators([]);
    } finally {
      setIsLoadingCoordinators(false);
    }
  };

  // Fetch all clients
  const fetchClients = async () => {
    setIsLoadingClients(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(API_ENDPOINTS.CLIENTS_LIST, { headers });
      const clientsData = Array.isArray(response.data)
        ? response.data
        : (response.data?.results || response.data?.data || response.data?.clients || []);
      setClients(clientsData);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setClients([]);
    } finally {
      setIsLoadingClients(false);
    }
  };

  // Calculate unassigned clients from existing data
  const calculateUnassignedClients = useCallback((clientsList, assignmentsList) => {
    if (!clientsList || clientsList.length === 0) {
      return [];
    }
    
    if (!assignmentsList || assignmentsList.length === 0) {
      return clientsList; // If no assignments, all clients are unassigned
    }

    // Get all assigned client IDs
    const assignedClientIds = new Set(
      assignmentsList
        .filter(a => a.is_active !== false) // Only count active assignments
        .map(a => {
          const clientId = a?.client_details?.id || 
                          a?.client?.id || 
                          a?.client_id ||
                          (typeof a?.client === 'number' ? a.client : null);
          return clientId;
        })
        .filter(id => id != null)
    );

    // Return clients that are not assigned
    return clientsList.filter(client => !assignedClientIds.has(client.id));
  }, []);

  // Fetch unassigned clients (optional endpoint - gracefully handle 404)
  const fetchUnassignedClients = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await silentAxiosGet(API_ENDPOINTS.CLIENTS_UNASSIGNED, { headers });
      
      if (response.status === 404) {
        // Endpoint doesn't exist - will be calculated from existing data in useEffect
        return;
      }
      
      const unassignedData = Array.isArray(response.data)
        ? response.data
        : (response.data?.results || response.data?.data || []);
      setUnassignedClients(unassignedData);
    } catch (err) {
      // Silently handle - will be calculated from existing data in useEffect
    }
  };

  // Helper function to extract coordinator name from assignment
  const getCoordinatorName = (assignment) => {
    // Check coordinator_details first (based on actual API response)
    if (assignment?.coordinator_details?.username) return assignment.coordinator_details.username;
    if (assignment?.coordinator_details?.user_detail?.username) return assignment.coordinator_details.user_detail.username;
    if (assignment?.coordinator_details?.name) return assignment.coordinator_details.name;
    // Fallback to coordinator object
    if (assignment?.coordinator?.username) return assignment.coordinator.username;
    if (assignment?.coordinator?.user_detail?.username) return assignment.coordinator.user_detail.username;
    if (assignment?.coordinator?.name) return assignment.coordinator.name;
    // Check other possible fields
    if (assignment?.coordinator_name) return assignment.coordinator_name;
    if (assignment?.coordinator_username) return assignment.coordinator_username;
    if (typeof assignment?.coordinator === 'string') return assignment.coordinator;
    // If coordinator is just an ID, try to find it in coordinators list
    if (typeof assignment?.coordinator === 'number' && coordinators.length > 0) {
      const coord = coordinators.find(c => c.id === assignment.coordinator);
      if (coord) return coord.username;
    }
    return 'N/A';
  };

  // Helper function to extract client name from assignment
  const getClientName = (assignment) => {
    // Check client_details first (based on actual API response)
    if (assignment?.client_details?.name) return assignment.client_details.name;
    if (assignment?.client_details?.client_name) return assignment.client_details.client_name;
    // Fallback to client object
    if (assignment?.client?.name) return assignment.client.name;
    if (assignment?.client?.client_name) return assignment.client.client_name;
    // Check other possible fields
    if (assignment?.client_name) return assignment.client_name;
    if (assignment?.client?.id) return `Client ${assignment.client.id}`;
    if (assignment?.client_id) return `Client ${assignment.client_id}`;
    if (typeof assignment?.client === 'string') return assignment.client;
    // If client is just an ID, try to find it in clients list
    if (typeof assignment?.client === 'number' && clients.length > 0) {
      const client = clients.find(c => c.id === assignment.client);
      if (client) return client.name || client.client_name || `Client ${client.id}`;
    }
    return 'N/A';
  };

  // Fetch all assignments with filters
  const fetchAssignments = async () => {
    setIsLoading(true);
    setError('');
    try {
      const headers = getAuthHeaders();
      const params = new URLSearchParams();
      
      if (filters.coordinator_id) {
        params.append('coordinator_id', filters.coordinator_id);
      }
      if (filters.client_id) {
        params.append('client_id', filters.client_id);
      }
      if (filters.is_active !== 'all') {
        params.append('is_active', filters.is_active === 'true');
      }

      const url = params.toString()
        ? `${API_ENDPOINTS.COORDINATORS_ASSIGNMENTS_LIST}?${params.toString()}`
        : API_ENDPOINTS.COORDINATORS_ASSIGNMENTS_LIST;

      const response = await axios.get(url, { headers });
      const assignmentsData = Array.isArray(response.data)
        ? response.data
        : (response.data?.results || response.data?.data || response.data?.assignments || []);

      // Log for debugging if needed (commented out to reduce console noise)
      // if (assignmentsData.length > 0 && process.env.NODE_ENV === 'development') {
      //   console.log('Sample assignment data:', assignmentsData[0]);
      // }

      setAssignments(assignmentsData);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch assignments'));
      setAssignments([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch coordinator's clients
  const fetchCoordinatorClients = async (coordinatorId) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(API_ENDPOINTS.COORDINATORS_CLIENTS(coordinatorId), { headers });
      const clientsData = Array.isArray(response.data)
        ? response.data
        : (response.data?.results || response.data?.data || []);
      return clientsData;
    } catch (err) {
      console.error('Error fetching coordinator clients:', err);
      return [];
    }
  };

  // Calculate dashboard stats from existing data
  const calculateDashboardStats = useCallback((assignmentsList, coordinatorsList, clientsList, unassignedClientsList) => {
    const activeAssignments = (assignmentsList || []).filter(a => a.is_active !== false);
    const inactiveAssignments = (assignmentsList || []).filter(a => a.is_active === false);
    
    return {
      total_assignments: (assignmentsList || []).length,
      active_assignments: activeAssignments.length,
      inactive_assignments: inactiveAssignments.length,
      total_coordinators: (coordinatorsList || []).length,
      total_clients: (clientsList || []).length,
      unassigned_clients: (unassignedClientsList || []).length
    };
  }, []);

  // Fetch dashboard stats (optional endpoint - gracefully handle 403/404)
  const fetchDashboardStats = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await silentAxiosGet(API_ENDPOINTS.COORDINATORS_DASHBOARD_STATS, { headers });
      
      if (response.status === 403 || response.status === 404) {
        // Endpoint not accessible - will be calculated from existing data in useEffect
        return;
      }
      
      setDashboardStats(response.data);
    } catch (err) {
      // Silently handle - will be calculated from existing data in useEffect
    }
  };

  // Calculate unassigned clients when clients or assignments change
  useEffect(() => {
    if (clients.length > 0) {
      const unassigned = calculateUnassignedClients(clients, assignments);
      setUnassignedClients(unassigned);
    } else {
      setUnassignedClients([]);
    }
  }, [clients, assignments, calculateUnassignedClients]);

  // Calculate dashboard stats when data changes
  useEffect(() => {
    const calculatedStats = calculateDashboardStats(assignments, coordinators, clients, unassignedClients);
    setDashboardStats(calculatedStats);
  }, [assignments, coordinators, clients, unassignedClients, calculateDashboardStats]);

  // Initial data fetch
  useEffect(() => {
    fetchCoordinators();
    fetchClients();
    fetchAssignments();
    // Try to fetch from API, but will fallback to calculated values
    fetchUnassignedClients();
    fetchDashboardStats();
  }, []);

  // Refetch assignments when filters change
  useEffect(() => {
    fetchAssignments();
  }, [filters]);

  // Filtered assignments based on search
  const filteredAssignments = useMemo(() => {
    if (!searchTerm) return assignments;

    const searchLower = searchTerm.toLowerCase();
    return assignments.filter(assignment => {
      const coordinatorName = getCoordinatorName(assignment);
      const clientName = getClientName(assignment);
      const notes = assignment?.notes || '';
      
      return (
        coordinatorName.toLowerCase().includes(searchLower) ||
        clientName.toLowerCase().includes(searchLower) ||
        notes.toLowerCase().includes(searchLower)
      );
    });
  }, [assignments, searchTerm]);

  // Handle create assignment
  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    if (!createForm.coordinator_id || !createForm.client_ids || createForm.client_ids.length === 0) {
      setError('Please select both coordinator and at least one client');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const headers = getAuthHeaders();
      let successCount = 0;
      let errorCount = 0;

      // Create assignments for each selected client
      for (const clientId of createForm.client_ids) {
        try {
          const payload = {
            coordinator_id: parseInt(createForm.coordinator_id),
            client_id: parseInt(clientId),
            notes: createForm.notes || ''
          };

          await axios.post(API_ENDPOINTS.COORDINATORS_ASSIGN_CLIENT, payload, { headers });
          successCount++;
        } catch (err) {
          errorCount++;
          console.error(`Failed to assign client ${clientId}:`, err);
        }
      }

      if (successCount > 0) {
        setSuccessMessage(
          errorCount > 0
            ? `${successCount} client(s) assigned successfully. ${errorCount} failed.`
            : `${successCount} client(s) assigned to coordinator successfully`
        );
        setIsCreateModalOpen(false);
        setCreateForm({ coordinator_id: '', client_ids: [], notes: '' });
        
        // Refresh data
        fetchAssignments();
        fetchUnassignedClients();
        fetchDashboardStats();
      } else {
        setError('Failed to assign any clients. Please try again.');
      }
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to assign clients to coordinator'));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle update assignment
  const handleUpdateAssignment = async (e) => {
    e.preventDefault();
    if (!selectedAssignment?.id || !editForm.coordinator_id || !editForm.client_ids || editForm.client_ids.length === 0) {
      setError('Please select both coordinator and at least one client');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const headers = getAuthHeaders();
      
      // Get original coordinator and clients
      const originalCoordinatorId = selectedAssignment?.coordinator_details?.id ||
                                    selectedAssignment?.coordinator?.id || 
                                    selectedAssignment?.coordinator_id ||
                                    (typeof selectedAssignment?.coordinator === 'number' ? selectedAssignment.coordinator : null);
      
      // Get current client from the assignment being edited
      const currentClientId = selectedAssignment?.client_details?.id ||
                              selectedAssignment?.client?.id || 
                              selectedAssignment?.client_id ||
                              (typeof selectedAssignment?.client === 'number' ? selectedAssignment.client : null);
      
      // Fetch current assignments for this coordinator
      let currentAssignments = [];
      if (originalCoordinatorId) {
        try {
          const coordinatorClients = await fetchCoordinatorClients(originalCoordinatorId);
          currentAssignments = coordinatorClients.map(c => ({
            id: c?.id || c?.assignment_id,
            client_id: c?.client?.id || c?.client_id || c?.client_details?.id || (typeof c?.client === 'number' ? c.client : null)
          })).filter(a => a.id && a.client_id);
          
          // If no assignments found from API but we have current assignment, add it
          if (currentAssignments.length === 0 && selectedAssignment?.id && currentClientId) {
            currentAssignments.push({
              id: selectedAssignment.id,
              client_id: currentClientId
            });
          }
        } catch (err) {
          console.warn('Error fetching current assignments:', err);
          // Fallback: use the current assignment if API fails
          if (selectedAssignment?.id && currentClientId) {
            currentAssignments.push({
              id: selectedAssignment.id,
              client_id: currentClientId
            });
          }
        }
      } else if (selectedAssignment?.id && currentClientId) {
        // If we can't get coordinator ID, at least use the current assignment
        currentAssignments.push({
          id: selectedAssignment.id,
          client_id: currentClientId
        });
      }
      
      const newCoordinatorId = parseInt(editForm.coordinator_id);
      const newClientIds = editForm.client_ids.map(id => parseInt(id));
      const originalCoordinatorIdNum = originalCoordinatorId ? parseInt(originalCoordinatorId) : null;
      const coordinatorChanged = originalCoordinatorIdNum !== newCoordinatorId;
      
      let successCount = 0;
      let errorCount = 0;
      let deletedCount = 0;
      let updatedCount = 0;
      
      // Normalize current client IDs to numbers for comparison
      const currentClientIds = currentAssignments.map(a => {
        const clientId = a.client_id;
        return typeof clientId === 'number' ? clientId : parseInt(clientId);
      });
      
      if (coordinatorChanged) {
        // Coordinator changed - delete all old assignments and create new ones
        // Delete old assignments
        for (const assignment of currentAssignments) {
          try {
            await axios.delete(
              API_ENDPOINTS.COORDINATORS_ASSIGNMENT_DELETE(assignment.id),
              { headers }
            );
            deletedCount++;
          } catch (err) {
            console.warn(`Failed to delete assignment ${assignment.id}:`, err);
          }
        }
        
        // Create new assignments with new coordinator
        for (const clientId of newClientIds) {
          try {
            const payload = {
              coordinator_id: newCoordinatorId,
              client_id: clientId,
              notes: editForm.notes || ''
            };
            const response = await axios.post(API_ENDPOINTS.COORDINATORS_ASSIGN_CLIENT, payload, { headers });
            const newAssignmentId = response.data?.id || response.data?.data?.id;
            
            // Update status if needed
            if (newAssignmentId && !editForm.is_active) {
              try {
                await axios.put(
                  API_ENDPOINTS.COORDINATORS_ASSIGNMENT_UPDATE(newAssignmentId),
                  { is_active: false },
                  { headers }
                );
              } catch (err) {
                console.warn(`Failed to update status for assignment ${newAssignmentId}:`, err);
              }
            }
            successCount++;
          } catch (err) {
            errorCount++;
            console.error(`Failed to assign client ${clientId}:`, err);
          }
        }
      } else {
        // Coordinator same - add new clients and remove deleted ones
        const clientsToAdd = newClientIds.filter(id => !currentClientIds.includes(id));
        const clientsToRemove = currentAssignments.filter(a => {
          const clientId = typeof a.client_id === 'number' ? a.client_id : parseInt(a.client_id);
          return !newClientIds.includes(clientId);
        });
        
        // Remove clients that are no longer selected
        for (const assignment of clientsToRemove) {
          try {
            await axios.delete(
              API_ENDPOINTS.COORDINATORS_ASSIGNMENT_DELETE(assignment.id),
              { headers }
            );
            deletedCount++;
          } catch (err) {
            errorCount++;
            console.error(`Failed to delete assignment ${assignment.id}:`, err);
          }
        }
        
        // Add new clients
        for (const clientId of clientsToAdd) {
          try {
            const payload = {
              coordinator_id: newCoordinatorId,
              client_id: clientId,
              notes: editForm.notes || ''
            };
            const response = await axios.post(API_ENDPOINTS.COORDINATORS_ASSIGN_CLIENT, payload, { headers });
            const newAssignmentId = response.data?.id || response.data?.data?.id;
            
            // Update status if needed
            if (newAssignmentId && !editForm.is_active) {
              try {
                await axios.put(
                  API_ENDPOINTS.COORDINATORS_ASSIGNMENT_UPDATE(newAssignmentId),
                  { is_active: false },
                  { headers }
                );
              } catch (err) {
                console.warn(`Failed to update status for assignment ${newAssignmentId}:`, err);
              }
            }
            successCount++;
          } catch (err) {
            errorCount++;
            console.error(`Failed to assign client ${clientId}:`, err);
          }
        }
        
        // Update notes and status for existing assignments that remain
        const assignmentsToUpdate = currentAssignments.filter(a => {
          const clientId = typeof a.client_id === 'number' ? a.client_id : parseInt(a.client_id);
          return newClientIds.includes(clientId);
        });
        
        const originalNotes = selectedAssignment?.notes || '';
        const notesChanged = editForm.notes !== originalNotes;
        
        // Check if we need to update status by comparing with current assignment
        const originalIsActive = selectedAssignment?.is_active !== false;
        const statusChanged = originalIsActive !== editForm.is_active;
        
        // Always update existing assignments if notes or status changed, or if clients were added/removed
        // This ensures updates happen even if only notes/status changed
        if (assignmentsToUpdate.length > 0) {
          for (const assignment of assignmentsToUpdate) {
            try {
              const updatePayload = {};
              
              // Always update status (in case it changed)
              updatePayload.is_active = editForm.is_active;
              
              // Always update notes (in case it changed)
              updatePayload.notes = editForm.notes || '';
              
              await axios.put(
                API_ENDPOINTS.COORDINATORS_ASSIGNMENT_UPDATE(assignment.id),
                updatePayload,
                { headers }
              );
              updatedCount++;
            } catch (err) {
              errorCount++;
              console.error(`Failed to update assignment ${assignment.id}:`, err);
              setError(getErrorMessage(err, `Failed to update assignment ${assignment.id}`));
            }
          }
        } else if (currentAssignments.length === 0 && newClientIds.length > 0) {
          // If no current assignments found but we have clients to assign, create them
          for (const clientId of newClientIds) {
            try {
              const payload = {
                coordinator_id: newCoordinatorId,
                client_id: clientId,
                notes: editForm.notes || ''
              };
              const response = await axios.post(API_ENDPOINTS.COORDINATORS_ASSIGN_CLIENT, payload, { headers });
              const newAssignmentId = response.data?.id || response.data?.data?.id;
              
              // Update status if needed
              if (newAssignmentId && !editForm.is_active) {
                try {
                  await axios.put(
                    API_ENDPOINTS.COORDINATORS_ASSIGNMENT_UPDATE(newAssignmentId),
                    { is_active: false },
                    { headers }
                  );
                } catch (err) {
                  console.warn(`Failed to update status for assignment ${newAssignmentId}:`, err);
                }
              }
              successCount++;
            } catch (err) {
              errorCount++;
              console.error(`Failed to assign client ${clientId}:`, err);
            }
          }
        }
      }

      // Check if any operations were successful
      if (successCount > 0 || deletedCount > 0 || updatedCount > 0) {
        const messages = [];
        if (deletedCount > 0) messages.push(`${deletedCount} client(s) removed`);
        if (successCount > 0) messages.push(`${successCount} client(s) added`);
        if (updatedCount > 0) messages.push(`${updatedCount} assignment(s) updated`);
        if (errorCount > 0) messages.push(`${errorCount} operation(s) failed`);
        
        setSuccessMessage(
          messages.length > 0 
            ? messages.join('. ') + '.'
            : 'Assignment updated successfully'
        );
        setIsEditModalOpen(false);
        setSelectedAssignment(null);
        
        // Refresh data
        fetchAssignments();
        fetchUnassignedClients();
        fetchDashboardStats();
      } else {
        // Provide more detailed error message
        if (errorCount > 0) {
          setError(`Failed to update assignment. ${errorCount} operation(s) failed. Please check the console for details.`);
        } else {
          setError('No changes were detected. Please make sure you have selected at least one client and made changes.');
        }
      }
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update assignment'));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete assignment
  const handleDeleteAssignment = async () => {
    if (!selectedAssignment?.id) {
      setError('No assignment selected for deletion');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const headers = getAuthHeaders();
      const assignmentId = selectedAssignment.id;
      
      const response = await axios.delete(
        API_ENDPOINTS.COORDINATORS_ASSIGNMENT_DELETE(assignmentId),
        { headers }
      );

      // Handle response message
      const responseMessage = response?.data?.message || 'Assignment deactivated successfully';
      const isDeactivated = responseMessage.toLowerCase().includes('deactivated') || 
                           response?.data?.status === 'ok';

      // Optimistically update local state - mark assignment as inactive
      setAssignments(prev => prev.map(assignment => 
        assignment.id === assignmentId 
          ? { ...assignment, is_active: false }
          : assignment
      ));

      setSuccessMessage(isDeactivated 
        ? 'Assignment deactivated successfully' 
        : 'Assignment removed successfully');
      setIsDeleteModalOpen(false);
      setSelectedAssignment(null);
      
      // Refresh data to get latest state
      await fetchAssignments();
      fetchUnassignedClients();
      // Dashboard stats is optional, don't wait for it
      fetchDashboardStats().catch(() => {}); // Silently fail if 403
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      const errorMsg = getErrorMessage(err, 'Failed to remove assignment');
      setError(errorMsg);
      // Don't close modal on error so user can see the error and try again
    } finally {
      setIsLoading(false);
    }
  };

  // Open edit modal
  const handleOpenEdit = async (assignment) => {
    setSelectedAssignment(assignment);
    
    // Extract coordinator ID
    const coordinatorId = assignment?.coordinator_details?.id ||
                          assignment?.coordinator?.id || 
                          assignment?.coordinator_id ||
                          (typeof assignment?.coordinator === 'number' ? assignment.coordinator : null);
    
    // Extract current client ID
    const currentClientId = assignment?.client_details?.id ||
                            assignment?.client?.id || 
                            assignment?.client_id ||
                            (typeof assignment?.client === 'number' ? assignment.client : null);
    
    // Fetch all clients for this coordinator
    let coordinatorClients = [];
    if (coordinatorId) {
      try {
        coordinatorClients = await fetchCoordinatorClients(coordinatorId);
      } catch (err) {
        console.warn('Error fetching coordinator clients:', err);
      }
    }
    
    // Get all client IDs assigned to this coordinator
    const assignedClientIds = coordinatorClients
      .map(c => c?.client?.id || c?.client_id || c?.client_details?.id || (typeof c?.client === 'number' ? c.client : null))
      .filter(id => id != null)
      .map(id => String(id));
    
    // If current client is not in the list, add it
    if (currentClientId && !assignedClientIds.includes(String(currentClientId))) {
      assignedClientIds.push(String(currentClientId));
    }
    
    setEditForm({
      coordinator_id: coordinatorId ? String(coordinatorId) : '',
      client_ids: assignedClientIds.length > 0 ? assignedClientIds : (currentClientId ? [String(currentClientId)] : []),
      notes: assignment?.notes || '',
      is_active: assignment?.is_active !== false
    });
    setIsEditModalOpen(true);
  };

  // Open view modal (coordinator's clients)
  const handleOpenView = async (coordinator) => {
    setSelectedCoordinator(coordinator);
    setIsViewModalOpen(true);
  };

  // Open delete modal
  const handleOpenDelete = (assignment) => {
    if (!assignment?.id) {
      setError('Invalid assignment selected for deletion');
      return;
    }
    setSelectedAssignment(assignment);
    setIsDeleteModalOpen(true);
    setError(''); // Clear any previous errors
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      coordinator_id: '',
      client_id: '',
      is_active: 'all'
    });
    setSearchTerm('');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Coordinator Client Assignment</h1>
                <p className="text-muted-foreground mt-1">Manage coordinator-client assignments</p>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    fetchAssignments();
                    fetchUnassignedClients();
                    fetchDashboardStats();
                  }}
                  iconName="RefreshCw"
                >
                  Refresh
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    setCreateForm({ coordinator_id: '', client_ids: [], notes: '' });
                    setIsCreateModalOpen(true);
                  }}
                  iconName="UserPlus"
                >
                  Assign Client
                </Button>
              </div>
            </div>
          </div>

          {/* Messages */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <p className="text-red-800">{error}</p>
              </div>
              <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <p className="text-green-800">{successMessage}</p>
              </div>
              <button onClick={() => setSuccessMessage('')} className="text-green-600 hover:text-green-800">
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Dashboard Stats */}
          {dashboardStats && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="p-6 rounded-lg border border-blue-200 bg-blue-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 mb-1">Total Assignments</p>
                    <p className="text-3xl font-bold text-blue-900">
                      {dashboardStats.total_assignments || 0}
                    </p>
                  </div>
                  <Users className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <div className="p-6 rounded-lg border border-green-200 bg-green-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 mb-1">Active Assignments</p>
                    <p className="text-3xl font-bold text-green-900">
                      {dashboardStats.active_assignments || 0}
                    </p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <div className="p-6 rounded-lg border border-purple-200 bg-purple-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 mb-1">Total Coordinators</p>
                    <p className="text-3xl font-bold text-purple-900">
                      {dashboardStats.total_coordinators || coordinators.length}
                    </p>
                  </div>
                  <Users className="w-8 h-8 text-purple-600" />
                </div>
              </div>
              <div className="p-6 rounded-lg border border-orange-200 bg-orange-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-600 mb-1">Unassigned Clients</p>
                    <p className="text-3xl font-bold text-orange-900">
                      {unassignedClients.length}
                    </p>
                  </div>
                  <AlertCircle className="w-8 h-8 text-orange-600" />
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-card rounded-lg border border-border p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground flex items-center space-x-2">
                <Filter className="w-5 h-5" />
                <span>Filters</span>
              </h2>
              <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coordinator</label>
                <select
                  value={filters.coordinator_id}
                  onChange={(e) => setFilters({ ...filters, coordinator_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Coordinators</option>
                  {coordinators.map((coord) => (
                    <option key={coord.id} value={coord.id}>
                      {coord.username}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                <select
                  value={filters.client_id}
                  onChange={(e) => setFilters({ ...filters, client_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Clients</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name || client.client_name || `Client ${client.id}`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={filters.is_active}
                  onChange={(e) => setFilters({ ...filters, is_active: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">All</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search assignments..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Assignments Table */}
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Coordinator
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Assigned Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Notes
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border">
                  {isLoading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-muted-foreground">
                        <div className="flex items-center justify-center space-x-2">
                          <RefreshCw className="w-5 h-5 animate-spin" />
                          <span>Loading assignments...</span>
                        </div>
                      </td>
                    </tr>
                  ) : filteredAssignments.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-muted-foreground">
                        No assignments found
                      </td>
                    </tr>
                  ) : (
                    filteredAssignments.map((assignment) => (
                      <tr key={assignment.id} className="hover:bg-muted/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-foreground">
                              {getCoordinatorName(assignment)}
                            </span>
                            <button
                              onClick={() => {
                                // Extract coordinator ID from various possible structures
                                const coordinatorId = assignment?.coordinator_details?.id ||
                                                      assignment?.coordinator?.id || 
                                                      assignment?.coordinator_id ||
                                                      (typeof assignment?.coordinator === 'number' ? assignment.coordinator : null) ||
                                                      (assignment?.coordinator && typeof assignment.coordinator === 'object' ? assignment.coordinator.id : null);
                                if (coordinatorId) {
                                  handleOpenView({ 
                                    id: coordinatorId, 
                                    username: getCoordinatorName(assignment) 
                                  });
                                }
                              }}
                              className="text-primary hover:text-primary-dark"
                              title="View coordinator's clients"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-foreground">
                            {getClientName(assignment)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              assignment?.is_active !== false
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {assignment?.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {assignment?.assigned_at || assignment?.created_at
                            ? new Date(assignment.assigned_at || assignment.created_at).toLocaleDateString()
                            : 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-muted-foreground line-clamp-2 max-w-xs">
                            {assignment?.notes || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleOpenEdit(assignment)}
                              className="text-primary hover:text-primary-dark"
                              title="Edit assignment"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleOpenDelete(assignment);
                              }}
                              className="text-red-600 hover:text-red-800 transition-colors relative group"
                              title="It's a soft delete option it just make the user inactive"
                            >
                              <Trash2 className="w-4 h-4" />
                              {/* Tooltip */}
                              <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                It's a soft delete option it just make the user inactive
                                <span className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></span>
                              </span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Create Assignment Modal */}
          {isCreateModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className="bg-card rounded-lg shadow-xl w-full max-w-md border border-border">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-foreground">Assign Client to Coordinator</h3>
                  <button
                    onClick={() => setIsCreateModalOpen(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={handleCreateAssignment} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Coordinator <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={createForm.coordinator_id}
                      onChange={(e) => setCreateForm({ ...createForm, coordinator_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    >
                      <option value="">Select coordinator</option>
                      {coordinators.map((coord) => (
                        <option key={coord.id} value={coord.id}>
                          {coord.username} ({coord.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Clients <span className="text-red-500">*</span> (Select multiple)
                    </label>
                    <select
                      multiple
                      value={createForm.client_ids}
                      onChange={(e) => {
                        const selectedIds = Array.from(e.target.selectedOptions, option => option.value);
                        setCreateForm({ ...createForm, client_ids: selectedIds });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary min-h-[120px]"
                      required
                      size={5}
                    >
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.name || client.client_name || `Client ${client.id}`}
                        </option>
                      ))}
                    </select>
                    {createForm.client_ids.length > 0 && (
                      <p className="mt-1 text-sm text-muted-foreground">
                        {createForm.client_ids.length} client(s) selected
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={createForm.notes}
                      onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Optional notes about this assignment..."
                    />
                  </div>
                  <div className="flex items-center justify-end space-x-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsCreateModalOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" variant="default" disabled={isLoading}>
                      {isLoading ? 'Assigning...' : `Assign ${createForm.client_ids.length > 0 ? `${createForm.client_ids.length} ` : ''}Client${createForm.client_ids.length !== 1 ? 's' : ''}`}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Edit Assignment Modal */}
          {isEditModalOpen && selectedAssignment && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className="bg-card rounded-lg shadow-xl w-full max-w-md border border-border">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-foreground">Edit Assignment</h3>
                  <button
                    onClick={() => setIsEditModalOpen(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={handleUpdateAssignment} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Coordinator <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={editForm.coordinator_id}
                      onChange={(e) => setEditForm({ ...editForm, coordinator_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    >
                      <option value="">Select coordinator</option>
                      {coordinators.map((coord) => (
                        <option key={coord.id} value={coord.id}>
                          {coord.username} ({coord.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Clients <span className="text-red-500">*</span> (Select multiple - add or remove)
                    </label>
                    <select
                      multiple
                      value={editForm.client_ids}
                      onChange={(e) => {
                        const selectedIds = Array.from(e.target.selectedOptions, option => option.value);
                        setEditForm({ ...editForm, client_ids: selectedIds });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary min-h-[120px]"
                      required
                      size={5}
                    >
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.name || client.client_name || `Client ${client.id}`}
                        </option>
                      ))}
                    </select>
                    {editForm.client_ids.length > 0 && (
                      <p className="mt-1 text-sm text-muted-foreground">
                        {editForm.client_ids.length} client(s) selected
                      </p>
                    )}
                    <p className="mt-1 text-xs text-muted-foreground">
                      Tip: Hold Ctrl/Cmd to select multiple clients. Deselect to remove.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={editForm.notes}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Optional notes about this assignment..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={editForm.is_active ? 'true' : 'false'}
                      onChange={(e) => setEditForm({ ...editForm, is_active: e.target.value === 'true' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-end space-x-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsEditModalOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" variant="default" disabled={isLoading}>
                      {isLoading ? 'Updating...' : `Update ${editForm.client_ids.length > 0 ? `${editForm.client_ids.length} ` : ''}Assignment${editForm.client_ids.length !== 1 ? 's' : ''}`}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* View Coordinator's Clients Modal */}
          {isViewModalOpen && selectedCoordinator && (
            <CoordinatorClientsModal
              coordinator={selectedCoordinator}
              onClose={() => {
                setIsViewModalOpen(false);
                setSelectedCoordinator(null);
              }}
              fetchCoordinatorClients={fetchCoordinatorClients}
              getAuthHeaders={getAuthHeaders}
            />
          )}

          {/* Soft Delete Confirmation Modal */}
          {isDeleteModalOpen && selectedAssignment && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className="bg-card rounded-lg shadow-xl w-full max-w-md border border-border">
                <div className="px-6 py-4 border-b border-border">
                  <h3 className="text-lg font-semibold text-foreground">Soft Delete Assignment</h3>
                </div>
                <div className="p-6">
                  {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  )}
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> This is a soft delete option. It will make the assignment inactive instead of permanently deleting it.
                    </p>
                  </div>
                  <p className="text-foreground mb-4">
                    Are you sure you want to soft delete the assignment between{' '}
                    <span className="font-semibold">
                      {getCoordinatorName(selectedAssignment)}
                    </span>{' '}
                    and{' '}
                    <span className="font-semibold">
                      {getClientName(selectedAssignment)}
                    </span>
                    ?
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Assignment ID: {selectedAssignment?.id}
                  </p>
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsDeleteModalOpen(false);
                        setSelectedAssignment(null);
                        setError('');
                      }}
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="default"
                      onClick={handleDeleteAssignment}
                      disabled={isLoading || !selectedAssignment?.id}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {isLoading ? 'Deactivating...' : 'Soft Delete'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

// Coordinator Clients Modal Component
const CoordinatorClientsModal = ({ coordinator, onClose, fetchCoordinatorClients, getAuthHeaders }) => {
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadClients = async () => {
      if (!coordinator?.id) return;
      setIsLoading(true);
      setError('');
      try {
        const clientsData = await fetchCoordinatorClients(coordinator.id);
        setClients(clientsData);
      } catch (err) {
        setError('Failed to load coordinator clients');
      } finally {
        setIsLoading(false);
      }
    };

    loadClients();
  }, [coordinator, fetchCoordinatorClients]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl border border-border max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              Clients Assigned to {coordinator?.username || 'Coordinator'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {coordinator?.email || ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading clients...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">{error}</div>
          ) : clients.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No clients assigned to this coordinator
            </div>
          ) : (
            <div className="space-y-4">
              {clients.map((client) => (
                <div
                  key={client.id || client.client_id}
                  className="p-4 border border-border rounded-lg hover:bg-muted/50"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-foreground">
                        {client?.client?.name || client?.name || client?.client_name || `Client ${client.id || client.client_id}`}
                      </h4>
                      {client?.assigned_at && (
                        <p className="text-sm text-muted-foreground mt-1">
                          Assigned: {new Date(client.assigned_at).toLocaleDateString()}
                        </p>
                      )}
                      {client?.notes && (
                        <p className="text-sm text-muted-foreground mt-1">{client.notes}</p>
                      )}
                    </div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        client?.is_active !== false
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {client?.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoordinatorClientAssignment;

